import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useShallow } from "zustand/shallow";
import { useUnmount } from "react-use";

import { CarouselContent } from "components/Carousel/CarouselContent";
import { useProjectsStore } from "stores/projects.store";

import { DateWithTooltip } from "components/DateWithTooltip";
import { AttachmentLightbox } from "components/AttachmentLightbox/AttachmentLightbox";
import { getFileType } from "components/Carousel/getFileType";
import {
  useActionAttachmentsQuery,
  useActionSubmittedAttachmentsQuery,
  useAttachmentDetailQuery
} from "hooks/queries/attachments";
import { downloadAsDataUri } from "core/utils/aws/downloadAsDataUri";
import { downloadFile } from "core/utils/aws/downloadFile";

export interface Props {
  title: string;
}

export const Carousel = ({ title }: Props): JSX.Element => {
  const selectedProjectActionId = useProjectsStore(
    useShallow(state => state.selectedProjectActionId)
  );
  const {
    data: attachments,
    isError: isErrorAttachments,
    isLoading: isLoadingAttachments
  } = useActionAttachmentsQuery({
    actionId: selectedProjectActionId
  });
  const {
    data: submittedAttachments,
    isError: isErrorSubmittedAttachments,
    isLoading: isLoadingSubmittedAttachments
  } = useActionSubmittedAttachmentsQuery({
    actionId: selectedProjectActionId
  });
  const [attachmentIndex, setAttachmentIndex] = useState(0);
  const [imageToView, setImageToView] = useState<string | null>(null);

  const allAttachments = useMemo(() => {
    if (
      !isErrorAttachments &&
      !isErrorSubmittedAttachments &&
      !isLoadingAttachments &&
      !isLoadingSubmittedAttachments
    ) {
      return [...attachments, ...submittedAttachments];
    }
    return [];
  }, [
    attachments,
    submittedAttachments,
    isErrorAttachments,
    isErrorSubmittedAttachments,
    isLoadingAttachments,
    isLoadingSubmittedAttachments
  ]);

  const attachmentId = useMemo(() => allAttachments?.[attachmentIndex]?.id, [
    attachmentIndex,
    allAttachments
  ]);

  const {
    data: attachment,
    refetch: refetchAttachment
  } = useAttachmentDetailQuery({
    attachmentId
  });

  const hasAttachments = !!allAttachments.length;

  const isImage = getFileType(attachment?.original_file.type ?? "") === "image";

  const controller = new AbortController();
  useUnmount(() => {
    controller.abort();
  });

  useEffect(() => {
    setAttachmentIndex(0);
  }, [selectedProjectActionId]);

  const handleNext = (): void => {
    /* istanbul ignore else */
    if (allAttachments && attachmentIndex < allAttachments.length - 1) {
      setAttachmentIndex(attachmentIndex + 1);
    }
  };

  const handlePrev = (): void => {
    /* istanbul ignore else */
    if (attachmentIndex > 0) {
      setAttachmentIndex(attachmentIndex - 1);
    }
  };

  const handleDownload = async (): Promise<void> => {
    await refetchAttachment();

    await downloadFile({
      url: attachment?.original_file.url ?? "",
      headers: attachment?.original_file.download_headers,
      signal: controller.signal,
      filename: attachment?.original_file.name
    });
  };

  const downloadAndSetImage = async (): Promise<void> => {
    await refetchAttachment();
    if (attachment) {
      let url = attachment.original_file.url;
      let headers = attachment.original_file.download_headers;

      if (attachment.thumbnail_large) {
        url = attachment.thumbnail_large.url;
        headers = attachment.thumbnail_large.download_headers;
      }

      setImageToView(
        await downloadAsDataUri({
          url,
          headers,
          signal: controller.signal
        })
      );
    }
  };

  if (isErrorAttachments || isErrorSubmittedAttachments) {
    return (
      <Typography>An error occurred while fetching attachments</Typography>
    );
  }

  return (
    <>
      <Box component="section">
        <Typography variant="h5" component="h2">
          {title}
        </Typography>

        <CarouselContent
          hasAttachments={hasAttachments}
          attachment={attachment}
        />

        {!hasAttachments && (
          <Typography
            textAlign="center"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            width="100%"
          >
            No attachments
          </Typography>
        )}

        {hasAttachments && (
          <Stack gap={1}>
            <Stack
              alignItems="center"
              direction="row"
              justifyContent="space-between"
              width="100%"
            >
              <IconButton
                type="button"
                aria-label="Previous attachment"
                onClick={handlePrev}
                disabled={attachmentIndex === 0}
              >
                <ChevronLeftIcon />
              </IconButton>

              <Tooltip title={attachment?.original_file.name ?? ""}>
                <Typography textAlign="center" noWrap data-testid="name">
                  {attachment?.original_file.name}
                </Typography>
              </Tooltip>

              <IconButton
                type="button"
                aria-label="Next attachment"
                onClick={handleNext}
                disabled={attachmentIndex === (allAttachments?.length ?? 0) - 1}
              >
                <ChevronRightIcon />
              </IconButton>
            </Stack>

            <Typography textAlign="center" data-testid="description">
              Added by{" "}
              <strong>
                {attachment?.create_user.first_name ?? ""}
                &nbsp;
                {attachment?.create_user.last_name ?? ""}
              </strong>{" "}
              on{" "}
              <strong>
                <DateWithTooltip date={attachment?.change_date} />
              </strong>
            </Typography>

            <Stack direction="row" gap={2} justifyContent="center">
              <ButtonGroup>
                <Button
                  variant="outlined"
                  type="button"
                  disabled={!isImage}
                  onClick={async () => {
                    await downloadAndSetImage();
                  }}
                >
                  View
                </Button>
                <Button
                  variant="outlined"
                  type="button"
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </ButtonGroup>
            </Stack>
          </Stack>
        )}
      </Box>

      <AttachmentLightbox
        attachmentUrl={imageToView}
        onClose={() => setImageToView(null)}
      />
    </>
  );
};
